// components/ui/ProgressBar.tsx
import React from "react";

interface ProgressBarProps {
  current: number;
  total?: number;
  label?: string;
  showCount?: boolean;
  errors?: number;
  isComplete?: boolean;
  isActive?: boolean;
  className?: string;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  current,
  total,
  label = "Loading",
  showCount = true,
  errors = 0,
  isComplete = false,
  isActive = true,
  className = "",
}) => {
  const hasTotal = typeof total === "number" && total > 0;
  const percentage = hasTotal ? Math.round((current / total) * 100) : 0;
  const hasProgress = current > 0 || hasTotal;

  // For streaming without total, show minimal progress until we have actual progress
  const displayWidth = hasTotal
    ? `${Math.min(percentage, 100)}%`
    : isComplete
      ? "100%"
      : hasProgress
        ? "25%" // Show some progress when we have items
        : "0%"; // Start at 0% when no progress yet

  // Don't show the bar at all if we don't have progress or completion
  if (!isActive || (!hasProgress && !isComplete)) return null;

  return (
    <div
      className={`bg-blue-50 border border-blue-200 rounded-lg p-4 ${className}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-blue-900">{label}</span>
          {isComplete && (
            <span className="text-green-600">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          )}
        </div>

        <div className="text-sm text-blue-700">
          {showCount && (
            <>
              {hasTotal ? (
                <span>
                  {current}/{total} ({percentage}%)
                </span>
              ) : (
                // Only show count if we have meaningful progress
                hasProgress && (
                  <span>
                    {current} loaded{!isComplete && "..."}
                  </span>
                )
              )}
              {errors > 0 && (
                <span className="text-red-600 ml-2">({errors} failed)</span>
              )}
            </>
          )}
        </div>
      </div>

      <div className="w-full bg-blue-100 rounded-full h-2 overflow-hidden">
        <div
          className={`h-2 rounded-full transition-all duration-500 ease-out ${
            isComplete ? "bg-green-500" : "bg-blue-500"
          }`}
          style={{ width: displayWidth }}
        >
          {/* Animated stripe for indeterminate progress */}
          {!hasTotal && !isComplete && hasProgress && (
            <div
              className="h-full bg-linear-to-r from-transparent via-blue-300 to-transparent"
              style={{
                backgroundSize: "200% 100%",
                animation: "shimmer 2s infinite linear",
              }}
            />
          )}
        </div>
      </div>

      {!hasTotal && !isComplete && hasProgress && (
        <div className="mt-2 text-xs text-blue-600">
          Images are loading as they become available...
        </div>
      )}

      <style>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
    </div>
  );
};
